import {Component, ViewChild, Input, OnChanges, SimpleChanges, AfterViewInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {EngineStatisticsService, ResultDataStatisticsDto, SerieStatisitcDto, StatisticsConfig, Partner} from '@tj/tj-dmp-lib-api';
import {firstValueFrom} from 'rxjs';

import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexTitleSubtitle
} from "ng-apexcharts";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  title: ApexTitleSubtitle;
};

import { fr } from '../../../../../assets/i18n/locales/locales';

@Component({
    selector: 'dmp-statistics-graph',
    templateUrl: './statistics-graph.component.html',
    styleUrls: ['./statistics-graph.component.scss'],
    standalone: false
})


export class StatisticsGraphComponent implements OnChanges, AfterViewInit {
  @ViewChild("chart") chart: ChartComponent;
  @Input() codeTenant: string;
  @Input() config: StatisticsConfig;
  @Input() partners: Array<Partner> = [];
  public isDynamic  = false;
  public isLoaded = false;
  public data: ResultDataStatisticsDto = null;
  public dataPerSerie = {};
  customStartDate = localStorage.getItem("customStartDate") ? new Date(localStorage.getItem("customStartDate")) : this.getLastThreeMonth();
  customEndDate = localStorage.getItem("customEndDate") ? new Date(localStorage.getItem("customEndDate")) : this.getToday();
  public activeSeries = [];
  public activeSeriesDynamic: Array<SerieStatisitcDto> = [];
  public chartOptions : any;
  public activeOptionButton = "1w";
  private chartUpdatePending = false;
  public updateOptionsData = {
    "1d": {
      xaxis: {
        min: this.getYesterDay().getTime(),
        max: this.getToday().getTime()
      }
    },
    "1w": {
      xaxis: {
        min: this.getLastWeek().getTime(),
        max: this.getToday().getTime()
      }
    },
    "1m": {
      xaxis: {
        min: this.getLastMonth().getTime(),
        max: this.getToday().getTime()
      }
    },
    "6m": {
      xaxis: {
        min: this.getLastSixMonth().getTime(),
        max: this.getToday().getTime()
      }
    },
    "1y": {
      xaxis: {
        min: this.getLastYear().getTime(),
        max: this.getToday().getTime()
      }
    },
    "cust": {
      xaxis: {
        min: this.customStartDate.getTime(),
        max: this.customEndDate.getTime()
      }
    },
    all: {
      xaxis: {
        min: new Date(2017,1,1).getTime(),
        max: new Date(new Date().getFullYear(), 12, 31).getTime()
      }
    }
  };


    constructor(private readonly translate: TranslateService,
                private readonly engineStatisticsService: EngineStatisticsService) { 
    }

    initChart(): void {
      this.chartOptions = {
        series: [
          {
            name: this.firstSerie(),
            data: []
          }
        ],
        chart: {
          type: "line",
          height: 350, 
          locales : [fr], 
          defaultLocale: "fr"
        },
        dataLabels: {
          enabled: false
        },
        markers: {
          size: 0
        },
        xaxis: {
          type: "datetime",
          min: this.getLastWeek().getTime(),
          tickAmount: 6
        },
        tooltip: {
          x: {
            format: "dd MMM yyyy"
          }
        }
      };
    }
    
    public firstSerie() : string {
      if (this.isDynamic) return this.data.series[0].label;
      return (this.config.series[0] == "NULL") ? "unit" : this.config.series[0];
    }

    public async updateOptions(option: any) {
      this.activeOptionButton = option;
      localStorage.setItem("activeOptionButton", option);
      if (option == "cust") {
        this.updateRangeOfChart();
      } else {
        this.isLoaded = false;
        if(!this.isDynamic){
          for (const serie of this.activeSeries) {
            await this.getData((serie=="unit") ? "NULL" : serie, new Date(this.updateOptionsData[this.activeOptionButton].xaxis.min), new Date(this.updateOptionsData[this.activeOptionButton].xaxis.max));
          }
          for (const serie of this.chartOptions.series) {
            for (const activeSerie of this.activeSeries) {
              if (serie.name == activeSerie) {
                serie.data = this.dataPerSerie[activeSerie];
              }
            }
          }
        }
        else {
          for (const serie of this.activeSeriesDynamic) {
            await this.getData(serie.code, new Date(this.updateOptionsData[this.activeOptionButton].xaxis.min), new Date(this.updateOptionsData[this.activeOptionButton].xaxis.max));
          }
          for (const serie of this.chartOptions.series) {
            for (const activeSerie of this.activeSeriesDynamic) {
              if (serie.name == activeSerie.label) {
                serie.data = this.dataPerSerie[activeSerie.code];
              }
            }
          }
        }

        this.updateChartView();
        this.isLoaded = true;
      }

    }
  
    public async updateSerie(serie: string) {
      serie = (serie=="NULL") ? "unit" : serie;
      if(!this.isSerieActive(serie)) {
        this.isLoaded = false;
        if(this.activeOptionButton == "cust") {
          await this.getData((serie=="unit") ? "NULL" : serie, this.customStartDate, this.customEndDate);
        } else {
          await this.getData((serie=="unit") ? "NULL" : serie, new Date(this.updateOptionsData[this.activeOptionButton].xaxis.min), new Date(this.updateOptionsData[this.activeOptionButton].xaxis.max));
        }
        this.chartOptions.series.push(
          {
            name: serie,
            data: this.dataPerSerie[serie]
          }
        );
        this.activeSeries.push(serie);
      } else {
        this.chartOptions.series = this.chartOptions.series.filter(chartSerie => chartSerie.name != serie);
        this.activeSeries = this.activeSeries.filter(activeSerie => activeSerie != serie);
      }
      this.updateChartView();
      localStorage.setItem("activeSeries", JSON.stringify(this.chartOptions.series));
      this.isLoaded = true;
    }

    public async updateSerieDynamic(serie: SerieStatisitcDto) {
      if(!this.isSerieActiveDynamic(serie)) {
        this.isLoaded = false;
        if(this.activeOptionButton == "cust") {
          await this.getData(serie.code, this.customStartDate, this.customEndDate);
        } else {
          await this.getData(serie.code, new Date(this.updateOptionsData[this.activeOptionButton].xaxis.min), new Date(this.updateOptionsData[this.activeOptionButton].xaxis.max));
        }
        this.chartOptions.series.push(
          {
            name: serie.label,
            data: this.dataPerSerie[serie.code]
          }
        );
        this.activeSeriesDynamic.push(serie);
      } else {
        this.chartOptions.series = this.chartOptions.series.filter(chartSerie => chartSerie.name != serie.label);
        this.activeSeriesDynamic = this.activeSeriesDynamic.filter(activeSerie => activeSerie.label != serie.label);
      }
      this.updateChartView();
      this.isLoaded = true;
    }

    public getSerieData(resultDataStatisticsDto : ResultDataStatisticsDto) : number[][] {
      const data = [];
      for (const serie of resultDataStatisticsDto.series) {
        for (const point of serie.data) {
          data.push([parseInt(point[0]), point[1]]);
        }
      }
      return data;
    }

    public getToday() : Date {
      return new Date();
    }

    public getYesterDay() : Date {
      return new Date(new Date().setDate(new Date().getDate() - 1));
    }

    public getLastWeek() : Date {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
    }

    public getLastMonth() : Date {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    }

    public getLastThreeMonth() : Date {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
    }

    public getLastSixMonth() : Date {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() - 6, today.getDate());
    }

    public getLastYear() : Date {
      const today = new Date();
      return new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    }

    public getDateByOption(option: string) : Date {
      switch (option) {
        case "1d":
          return this.getYesterDay();
        case "1w":
          return this.getLastWeek();
        case "1m":
          return this.getLastMonth();
        case "6m":
          return this.getLastSixMonth();
        case "1y":
          return this.getLastYear();
        case "all":
          return new Date(2017,1,1);
        case "cust":
          return localStorage.getItem("customStartDate") ? new Date(localStorage.getItem("customStartDate")) : this.getLastThreeMonth();
        default:
          return this.customStartDate ? this.customStartDate : this.getLastWeek();
      } 
    }

    public async updateRangeOfChart() {
      this.isLoaded = false;
      if(!this.isDynamic) {
        for (const serie of this.activeSeries) {
          if(!this.isDynamic) await this.getData((serie=="unit") ? "NULL" : serie, this.customStartDate, this.customEndDate);
        }
        for (const serie of this.chartOptions.series) {
          for (const activeSerie of this.activeSeries) {
            if (serie.name == activeSerie) {
              serie.data = this.dataPerSerie[activeSerie];
            }
          }
        }
      }
      if(this.isDynamic) {
        for (const serie of this.activeSeriesDynamic) {
          await this.getData(serie.code, this.customStartDate, this.customEndDate);
        }
        for (const serie of this.chartOptions.series) {
          for (const activeSerie of this.activeSeriesDynamic) {
            if (serie.name == activeSerie.label) {
              serie.data = this.dataPerSerie[activeSerie.code];
            }
          }
        }
      }
      this.chartOptions.xaxis.min = this.customStartDate.getTime();
      this.chartOptions.xaxis.max = this.customEndDate.getTime();
      this.updateChartView();
      localStorage.setItem("customStartDate", this.customStartDate.toISOString());
      localStorage.setItem("customEndDate", this.customEndDate.toISOString());
      this.isLoaded = true;
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (!changes.config || !this.config || !this.codeTenant) {
          return;
        }
        try {
          this.isLoaded = false;
          this.data = null;
          this.isDynamic = false;
          if (this.config.code == "DesabonnementDeLaNewsletter") {
            this.isDynamic = true;
            this.engineStatisticsService.getDataUsingPOST(this.codeTenant, this.config.code).subscribe(data => {
              this.data = data;
            });
            while(this.data == null) {
              await this.delay(50);
            }
          }
          this.initChart();
          await this.initializeData();
          if(!this.isDynamic) {
            this.activeSeries = [this.firstSerie()];
            this.chartOptions.series[0].data = this.dataPerSerie[this.firstSerie()];
          } else {
            this.activeSeriesDynamic = [this.data.series[0]];
            this.chartOptions.series[0].data = this.dataPerSerie[this.data.series[0].code];
          }
          this.chartUpdatePending = true;
          this.refreshChartIfReady();
        } catch (error) {
          console.error('Failed to load statistics chart', error);
          this.isLoaded = true;
        }
    }

    ngAfterViewInit(): void {
      this.refreshChartIfReady();
    }

    private refreshChartIfReady(): void {
      if (!this.chartUpdatePending || !this.chartOptions) {
        return;
      }
      if (!this.chart) {
        setTimeout(() => this.refreshChartIfReady(), 0);
        return;
      }
      this.activeOptionButton = localStorage.getItem("activeOptionButton") ? localStorage.getItem("activeOptionButton") : "1w";
      this.updateChartView();
      this.chartUpdatePending = false;
      this.isLoaded = true;
    }

    private updateChartView(): void {
      if (!this.chart || !this.chartOptions) {
        return;
      }
      this.chart.updateSeries(this.chartOptions.series);
      this.chart.updateOptions(this.updateOptionsData[this.activeOptionButton], false, true, true);
    }

    public dateToIsoString(date: Date) {
      return date.toISOString().split('T')[0];
    }

    public async getData(serie: string, startDate: Date, endDate: Date) {
      const resultDataStatisticsDto = await firstValueFrom(this.engineStatisticsService.getDataUsingPOST(this.codeTenant, this.config.code, this.dateToIsoString(endDate), this.partners.map(p=>p.code) ,this.dateToIsoString(startDate), {"serie":serie}));
      this.dataPerSerie[(serie=="NULL") ? "unit" : serie] = this.getSerieData(resultDataStatisticsDto);
    }

    public async initializeData() {
      if (this.isDynamic) await this.getData(this.data.series[0].code, this.getLastWeek(), this.getToday());
      else await this.getData(this.config.series[0],
         this.getDateByOption(localStorage.getItem("activeOptionButton") ? localStorage.getItem("activeOptionButton") : "1w"),
         this.getToday());
    }

    public isSerieActive(serie: string) {
      serie = (serie=="NULL") ? "unit" : serie;
      return this.activeSeries.includes(serie);
    }

    public isSerieActiveDynamic(serie: SerieStatisitcDto) {
      return this.activeSeriesDynamic.includes(serie);
    }

    public delay(ms: number) {
      return new Promise( resolve => setTimeout(resolve, ms) );
    }
  }

